import {Accordion, AccordionItem, Avatar, Card, CardBody, CardHeader, SelectItem} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {Icon} from "@iconify/react";
import {keepPreviousData, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useState} from "react";
import img_female from "../../../assets/img_female.png";
import img_male from "../../../assets/img_male.png";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import PSelect from "../../../components/PSelect.tsx";
import PTable from "../../../components/PTable.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";
import {Player} from "../../../types";
import {tableFullScreen} from "../../../user/courts/Edit.tsx";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/leaderboards-results/$ranking_group_uid')({
    component: RouteComponent,
})

function RouteComponent() {
    const params = useParams({strict: false});
    const queryClient = useQueryClient()

    const [filterText, setFilterText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [raceEntriesFilter, setRaceEntriesFilter] = useState('');

    const runningLeaderboardResultsQuery = useQuery({
        queryKey:        queryKeys.running.getLeaderboardResults(params.ranking_group_uid),
        queryFn:         async () => services.running.getLeaderboardResults(params.ranking_group_uid),
        enabled:         !!params.ranking_group_uid,
        placeholderData: keepPreviousData,
    })

    const size = useWindowSize();
    const isSm = !!size.width && size.width < 1024;

    const columns = (size.width && size.width < 1024
            ? [
                {key: 'combined', title: 'Player'},
            ]
            : [
                // ...(categoryFilter
                //         ? [{key: 'category_final_position', title: 'Category Position'}]
                //         : [{key: 'cumulativeposition', title: 'Cumulative Position'}]
                // ),
                {key: '_cumulative_position', title: 'Position'},
                {key: '_cumulative_points', title: 'Points'},
                {key: '_category_final_position', title: 'Category Position'},
                {key: 'name', title: 'Name'},
                {key: '_entries_count', title: 'Race Entries'},
                {key: 'countrycode', title: 'Country'},
                {key: 'gender', title: 'Gender'},
                {key: 'category', title: 'Category'},
            ]
    );

    if (!runningLeaderboardResultsQuery.isFetched) {
        return <>
            <Header />

            <Content className="flex justify-center my-6">
                <Spinner size={"lg"} />
            </Content>
        </>
    }

    const runningLeaderboardResultsQueryResults = (runningLeaderboardResultsQuery.data?.results || [])
        .filter((r) => !filterText || r.name.toLowerCase().includes(filterText.toLowerCase()))
        .filter((r) => !categoryFilter || r.category === categoryFilter)
        .filter((r) => !raceEntriesFilter || +r._entries_count === +raceEntriesFilter);

    return (
        <>
            <Header backUrl={"/running"} />
            <Content>

                <div className="w-full flex justify-between items-start sm:items-start mb-4">
                    <div className={"flex sm:flex-col"}>
                        <Accordion defaultExpandedKeys={isSm ? undefined : ['filters']}>
                            <AccordionItem key="filters" aria-label="Filters" title="Filters" classNames={{content: "flex items-end sm:flex-col sm:items-start gap-1", heading: (isSm ? '' : "hidden")}}>
                                <PInput
                                    label={"Name:"}
                                    labelPlacement={"outside-top"}
                                    className={"w-auto min-w-20"}
                                    onBlur={(e) => setFilterText(e.target.value)}
                                />
                                <PSelect
                                    placeholder={"category"}
                                    defaultSelectedKeys={[categoryFilter]}
                                    onSelectionChange={(e) => {
                                        setCategoryFilter(e.currentKey as any)
                                    }}
                                    label={"Category:"}
                                    multiple={true}
                                    disallowEmptySelection={true}
                                >
                                    <SelectItem key={""}></SelectItem>
                                    {(runningLeaderboardResultsQuery.data?.categories || []).map((category: any) => <SelectItem key={category}>{category}</SelectItem>)}
                                </PSelect>
                                <PSelect
                                    placeholder={"entries"}
                                    defaultSelectedKeys={[raceEntriesFilter]}
                                    onSelectionChange={(e) => {
                                        setRaceEntriesFilter(e.currentKey as any)
                                    }}
                                    label={"Race entries:"}
                                    multiple={true}
                                    disallowEmptySelection={true}
                                    className={"min-w-24"}
                                >
                                    <SelectItem key={""}></SelectItem>
                                    {(runningLeaderboardResultsQuery.data?.raceEntries || []).map((raceEntry: any) => <SelectItem key={String(raceEntry)}>{String(raceEntry)}</SelectItem>)}
                                </PSelect>
                                <PButton className={"ml-2 sm:mt-2"}>Search</PButton>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <PButton color="default" onPress={() => queryClient.invalidateQueries({queryKey: queryKeys.running.getLeaderboardResults()})}
                             isIconOnly={isSm}>
                        <Icon icon="material-symbols-light:refresh" className="text-2xl" /> {isSm ? '' : 'refresh'}
                    </PButton>
                </div>
                <Card>
                    <CardHeader className={"flex items-center justify-between"}>
                        <div className={"font-bold text-lg"}>Race Results</div>
                        <div>
                            {runningLeaderboardResultsQueryResults?.length} runners
                        </div>
                    </CardHeader>
                    <CardBody>
                        <PTable removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Running Results"}>
                            <TableHeader columns={columns}>
                                {(column) => <TableColumn key={column.key}>
                                    {column.title}
                                </TableColumn>}
                            </TableHeader>
                            <TableBody>
                                {runningLeaderboardResultsQueryResults.map((result: any) => <TableRow key={result.uid}>
                                    {(columnKey) => {
                                        if (columnKey === 'combined') {
                                            return <TableCell>
                                                <div className="flex w-full">
                                                    <div className={"flex items-center mr-2"}>
                                                        {result.cumulativeposition}.
                                                    </div>
                                                    <div>
                                                        <Avatar className={"mr-2"} src={result.profile_thumb_url || result.profile_image_url || (result.gender === "M" ? img_male : img_female)} />
                                                    </div>
                                                    <div>
                                                        <div className="flex-1 flex items-center">{result.name} {result.lastname}</div>
                                                        <small><img className="inline" src={'https://flagcdn.com/16x12/' + result.countrycode?.toLowerCase() + '.png'} title={result.countrycode} /> {result._entries_count} entries, {result.category}</small>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        }
                                        if (columnKey === 'name') {
                                            return <TableCell>
                                                <div className={"flex w-full items-center"}>
                                                    <Avatar className={"mr-2"} src={result.profile_thumb_url || result.profile_image_url || img_male} />
                                                    {result.name} {result.lastname}
                                                </div>
                                            </TableCell>
                                        }
                                        if (columnKey === 'gender') {
                                            return <TableCell>
                                                {result.gender === 'M' ? 'Male' : 'Female'}
                                            </TableCell>
                                        }
                                        if (columnKey === 'countrycode') {
                                            return <TableCell>
                                                <img className="inline" src={'https://flagcdn.com/16x12/' + result.countrycode?.toLowerCase() + '.png'} title={result.countrycode} />
                                            </TableCell>
                                        }
                                        if (result.hasOwnProperty(columnKey)) {
                                            return <TableCell>
                                                {result[columnKey] || '-'}
                                            </TableCell>
                                        }
                                        return (
                                            <TableCell>
                                                {columnKey}
                                            </TableCell>
                                        );
                                    }}
                                </TableRow>)}
                            </TableBody>

                        </PTable>
                    </CardBody>
                </Card>
            </Content>
        </>
    );
}
